/**
 * API client for The Differential.
 * Supports optional preview token for tester/early-access mode.
 */

const BASE = '/api';

async function request(method, path, body) {
  const opts = { method, headers: { 'Content-Type': 'application/json' } };
  if (body) opts.body = JSON.stringify(body);
  const res = await fetch(`${BASE}${path}`, opts);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Network error' }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function fetchGame(previewToken = null, dayOverride = null) {
  let qs = previewToken ? `?preview=${encodeURIComponent(previewToken)}` : '';
  if (dayOverride) qs += `${qs ? '&' : '?'}day=${dayOverride}`;
  return request('GET', `/game${qs}`);
}

export async function fetchSuggestions(q) {
  if (!q || q.trim().length < 1) return { suggestions: [] };
  const res = await fetch(`${BASE}/suggestions?q=${encodeURIComponent(q.trim())}`);
  if (!res.ok) return { suggestions: [] };
  return res.json();
}

export async function fetchDiseases() {
  const res = await fetch(`${BASE}/diseases`);
  if (!res.ok) return { diseases: [] };
  return res.json();
}

export async function sendAction(action, already_ordered = [], used_snark_indices = {}, previewToken = null, dayOverride = null) {
  const body = { action, already_ordered, used_snark_indices };
  if (previewToken) body.preview = previewToken;
  if (dayOverride) body.day = dayOverride;
  return request('POST', '/action', body);
}

export async function submitGuess(guess, moves, time_seconds, previewToken = null, dayOverride = null) {
  const body = { guess, moves, time_seconds };
  if (previewToken) body.preview = previewToken;
  if (dayOverride) body.day = dayOverride;
  return request('POST', '/guess', body);
}

export async function fetchGameByDay(day) {
  return request('GET', `/game/day/${day}`);
}

/**
 * Submits an anonymous game result to the global stats aggregator.
 * Returns a Promise<boolean> — true if the server confirmed receipt, false otherwise.
 * Uses keepalive so it completes even if the user closes the tab immediately.
 */
export async function submitStats(day, result, moves, time_seconds, score, grade) {
  try {
    const res = await fetch('/api/stats/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ day, result, moves, time_seconds, score, grade }),
      keepalive: true,
    });
    return res.ok; // true = server confirmed, false = server error
  } catch {
    return false; // network failure — caller should NOT mark as submitted
  }
}

/**
 * Fetches aggregate stats for a given day.
 * Returns { available: false, total_plays, threshold } when below threshold,
 * or { available: true, total_plays, wins, avg_moves, avg_time, avg_score, score_distribution }.
 */
export async function fetchStats(day) {
  try {
    const res = await fetch(`/api/stats/${day}`);
    if (!res.ok) return { available: false };
    return res.json();
  } catch {
    return { available: false };
  }
}

